/**
 * MutualConnectionsModal Component
 *
 * Displays a paginated list of mutual connections between the current user
 * and another user. Shows clickable avatars that navigate to user profiles.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { connectionsApi } from '@/services/api/connections';
import type { MutualConnection } from '@/types/connections';

interface MutualConnectionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
}

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a user
 */
const getDisplayName = (user: MutualConnection): string => {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.username;
};

const ITEMS_PER_PAGE = 20;

export const MutualConnectionsModal: React.FC<MutualConnectionsModalProps> = ({
  open,
  onOpenChange,
  userId,
  userName,
}) => {
  const navigate = useNavigate();

  const [connections, setConnections] = useState<MutualConnection[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMutualConnections = useCallback(async (pageNum: number) => {
    console.log('[MutualConnections] Fetching page:', pageNum, 'for user:', userId);
    setIsLoading(true);
    setError(null);

    try {
      const response = await connectionsApi.getMutualConnections(userId, {
        page: pageNum,
        limit: ITEMS_PER_PAGE,
      });

      if (response.success && response.data) {
        setConnections(response.data.connections);
        setTotal(response.data.count);
        setPages(response.data.pages || 1);
        console.log('[MutualConnections] Loaded:', response.data.connections.length, 'of', response.data.count);
      } else {
        setError('Failed to load mutual connections');
      }
    } catch (err) {
      console.error('[MutualConnections] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load mutual connections');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Fetch when modal opens or page changes
  useEffect(() => {
    if (open) {
      fetchMutualConnections(page);
    }
  }, [open, page, fetchMutualConnections]);

  // Reset page when modal opens
  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, userId]);

  const handleUserClick = (user: MutualConnection) => {
    console.log('[MutualConnections] Navigating to user profile:', user.username);
    onOpenChange(false);
    navigate(`/user/${user.username}`);
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < pages) {
      setPage(page + 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Mutual Connections with {userName}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2">
          {/* Total count */}
          {!isLoading && !error && (
            <p className="text-sm text-muted-foreground mb-4">
              {total} mutual connection{total !== 1 ? 's' : ''}
            </p>
          )}

          {/* Error state */}
          {error && (
            <div className="text-center py-8 text-destructive">
              <p className="text-sm">{error}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => fetchMutualConnections(page)}
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Loading state */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-3 p-2">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-1" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Connections list */}
          {!isLoading && !error && (
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {connections.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleUserClick(user)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-accent/50 transition-colors text-left"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user.avatar} alt={getDisplayName(user)} />
                    <AvatarFallback className="text-sm font-medium bg-primary/10 text-primary">
                      {getInitials(user.firstName, user.lastName, user.username)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{getDisplayName(user)}</p>
                    <p className="text-xs text-muted-foreground">@{user.username}</p>
                  </div>
                </button>
              ))}

              {connections.length === 0 && (
                <div className="text-center py-8">
                  <Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">No mutual connections found</p>
                </div>
              )}
            </div>
          )}

          {/* Pagination controls */}
          {!isLoading && !error && pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevPage}
                disabled={page <= 1}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>

              <span className="text-sm text-muted-foreground">
                Page {page} of {pages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={page >= pages}
                className="flex items-center gap-1"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MutualConnectionsModal;
